import { useMemo } from 'react';
import type { NoRoleViewer } from '../pages/new-designs/utils/teamRoles';
import ROLES from '../utils/roles';
import { useAdminAccess } from './useAdminAccess';
import { useProgramContext } from './useProgramContext';

/**
 * Who the signed-in member is, for the no-role mark (utils/teamRoles.ts): an
 * org admin, and the teams they manage. Read from GET /v3/admin/access, the
 * answer useCapabilityRelease and the routing already share (fetched once per
 * sign-in), so it sends no request of its own. A platform admin, or an org
 * admin by their role, is known at once; anyone else is null until the answer
 * comes, so the mark never flashes on an admin or a manager. When the call
 * failed they are a member who manages no team, the rule from before managers.
 */
export const useNoRoleViewer = (): NoRoleViewer | null => {
  const { isPlatformAdmin } = useProgramContext();
  const { access, loading } = useAdminAccess();
  const orgAdmin = isPlatformAdmin || localStorage.getItem('role') === ROLES.orgAdmin || Boolean(access?.orgAdmin);
  const managedTeamIds = access?.managedTeamIds;
  return useMemo(() => {
    if (orgAdmin) return { orgAdmin: true, managedTeamIds: [] };
    if (loading) return null;
    return { orgAdmin: false, managedTeamIds: managedTeamIds ?? [] };
  }, [orgAdmin, loading, managedTeamIds]);
};

export default useNoRoleViewer;
